import { ButtonV2, DateSelection, Flex } from '@withjoy/joykit';
import { useScheduleFormState } from '../../hooks/useScheduleFormState';
import type { ScheduleEditorData } from '../../ScheduleEditor.types';
import React, { useCallback, useState } from 'react';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { Skeleton } from '@shared/components/Skeleton';
import { Star, Trash } from '@withjoy/joykit/icons';
import { FormTextField } from './components/FormTextField';
import { ScheduleSection } from './components/ScheduleSection';
import { TimeDateSection } from './components/sections/TimeDateSection';
import { EventTypeSection } from './components/sections/EventTypeSection';
import { DressCodeSection } from './components/sections/DressCodeSection';
import { EventPrivacyInlineSection } from './components/sections/EventPrivacyInlineSection';
import { ConfirmationDialog } from '@shared/components/GeneralSchedule';
import { BottomActionsWrapper } from '../BottomActionsWrapper';

type ScheduleFormDisplayProps = {
  data: ScheduleEditorData;
  onSubmit: (state: ScheduleEditorData) => void;
  onCancel: () => void;
  onDelete?: (data: ScheduleEditorData) => void;
  isSaving?: boolean;
  eventId: string;
};

// Omit value from this to make optional.
const VALIDATE_FORM_DATA: Partial<Record<keyof ScheduleEditorData, (value: unknown, data: ScheduleEditorData) => boolean>> = {
  name: (value: unknown) => {
    const name = value as string;
    return name !== '';
  },
  timezone: (value: unknown, data: ScheduleEditorData) => {
    // Timezone is optional when both start and end times are not set (TBA option)
    if (!data.startTime && !data.endTime) {
      return true;
    }

    const tz = value as string;
    return tz !== '' && tz !== null;
  },
  startTime: (value: unknown, data: ScheduleEditorData) => {
    const startTime = value as DateSelection | null;
    const { endTime } = data;

    // Only validate when both start and end times are entered (Start + End option)
    // Start time and end time are optional (Start Only or TBA option)
    if (startTime && endTime) {
      const startTimeMs = startTime.getTime();
      const endTimeMs = endTime.getTime();
      // Check for invalid dates
      if (isNaN(startTimeMs) || isNaN(endTimeMs)) {
        return false;
      }
      return endTimeMs >= startTimeMs;
    }
    return true;
  },
  endTime: (value: unknown, data: ScheduleEditorData) => {
    const endTime = value as DateSelection | null;
    const { startTime } = data;

    // Only validate when both start and end times are entered (Start + End option)
    // Start time and end time are optional (Start Only or TBA option)
    if (startTime && endTime) {
      const startTimeMs = startTime.getTime();
      const endTimeMs = endTime.getTime();
      // Check for invalid dates
      if (isNaN(startTimeMs) || isNaN(endTimeMs)) {
        return false;
      }
      return endTimeMs >= startTimeMs;
    }
    return true;
  },
};

export type ConfirmationType = 'discard' | 'delete' | null;

/**
 * Schedule editor form. Data in, data out.
 */
export const ScheduleFormDisplay = (props: ScheduleFormDisplayProps) => {
  const { data, onSubmit, onCancel, onDelete, isSaving, eventId } = props;
  const { state, setValue, meta, setErrorFlag } = useScheduleFormState(data);
  const { t, tq } = useTranslation('scheduleEditor');
  const { value: scheduleItemsTaggingEnabled } = useFeatureValue('joyProAllowScheduleItemsTagging');
  const [showConfirm, setShowConfirm] = useState<ConfirmationType>(null);
  const trans = t('form');

  // Ensure all fields are valid
  const validate = useCallback(() => {
    const requiredFields = Object.entries(VALIDATE_FORM_DATA);

    // Test all required values to ensure valid
    return requiredFields.every(([k, validator]) => {
      const key = k as keyof ScheduleEditorData;
      const value = state[key];
      const isValid = validator(value, state);
      return isValid;
    });
  }, [state]);

  // Apply error flag to all fields which are invalid
  const flagInvalidFields = useCallback(() => {
    const requiredFields = Object.entries(VALIDATE_FORM_DATA);

    return requiredFields.forEach(([key, validator]) => {
      const value = state[key as keyof ScheduleEditorData];
      const isValid = validator(value, state);

      setErrorFlag(key as keyof ScheduleEditorData, !isValid);
    });
  }, [setErrorFlag, state]);

  const handleSubmit = useCallback(() => {
    const isValid = validate();
    flagInvalidFields();

    if (isValid) {
      onSubmit(state);
    }
  }, [flagInvalidFields, onSubmit, state, validate]);

  return (
    <Flex
      gap="56px"
      flexDirection="column"
    >
      <Flex
        gap="48px"
        flexDirection="column"
      >
        <Flex
          flexDirection="column"
          gap="48px"
        >
          <ScheduleSection
            icon={<Star size={24} />}
            title={tq`Basic Info`}
          >
            <FormTextField
              label={trans.nameFieldTitle()}
              placeholder={trans.nameFieldPlaceholder()}
              fieldName="name"
              state={state}
              meta={meta}
              setValue={setValue}
              maxLength={120}
            />
            <FormTextField
              label={trans.descriptionFieldTitle()}
              placeholder={trans.descriptionFieldPlaceholder()}
              fieldName="description"
              state={state}
              meta={meta}
              setValue={setValue}
              textField={true}
              maxLength={120}
            />
          </ScheduleSection>
          <TimeDateSection
            state={state}
            setValue={setValue as (key: keyof ScheduleEditorData, value: string | DateSelection) => void}
            meta={meta}
          />
          <EventTypeSection
            state={state}
            setValue={setValue as (key: keyof ScheduleEditorData, value: string | DateSelection) => void}
            meta={meta}
          />
          <DressCodeSection
            state={state}
            setValue={setValue}
            meta={meta}
          />
          {scheduleItemsTaggingEnabled === 'on' && (
            <EventPrivacyInlineSection
              state={state}
              setValue={setValue}
              meta={meta}
              eventId={eventId}
            />
          )}
        </Flex>
        {onDelete && (
          <ButtonV2
            startIcon={<Trash size={24} />}
            variant="ghost"
            intent="destructive"
            onClick={() => {
              setShowConfirm('delete');
            }}
          >
            {trans.destroyCTA()}
          </ButtonV2>
        )}
      </Flex>
      <Flex gap="16px">
        <ButtonV2
          variant="ghost"
          intent="neutral"
          width="100%"
          onClick={() => {
            setShowConfirm('discard');
          }}
        >
          {trans.secondaryCTA()}
        </ButtonV2>
        <ButtonV2
          intent="neutral"
          shape="rounded"
          width="100%"
          onClick={handleSubmit}
          loading={isSaving}
        >
          {trans.primaryCTA()}
        </ButtonV2>
      </Flex>
      <BottomActionsWrapper>
        <ConfirmationDialog
          mode={showConfirm}
          onDoCancel={() => {
            switch (showConfirm) {
              case 'delete': {
                onDelete?.(state);
                break;
              }
              case 'discard': {
                onCancel();
                break;
              }
            }
          }}
          setConfirmationType={setShowConfirm}
        />
      </BottomActionsWrapper>
    </Flex>
  );
};

const InputV2Skeleton = (props: { count?: number }) => {
  const { count = 1 } = props;

  return (
    <Flex
      flexDirection="column"
      gap="5px"
    >
      <Skeleton
        width={66}
        height={18}
        borderRadius={2}
      />
      {Array.from({ length: count }).map((_, i) => {
        return (
          <Skeleton
            key={i}
            width="100%"
            height={58}
            borderRadius="0.25rem"
          />
        );
      })}
    </Flex>
  );
};

export const ScheduleFormDisplaySkeleton = () => {
  const inputV2Length = 2;
  const datePickerLength = 2;

  return (
    <Flex
      flexDirection="column"
      gap="0.75rem"
    >
      {Array.from({ length: inputV2Length }).map((_, i) => {
        return <InputV2Skeleton key={i} />;
      })}
      {Array.from({ length: datePickerLength }).map((_, i) => {
        return (
          <InputV2Skeleton
            key={i}
            count={2}
          />
        );
      })}
      <Flex
        justifyContent="center"
        gap="0.5rem"
        marginTop="0.5rem"
        flexWrap={['wrap-reverse', 'nowrap']}
      >
        <Skeleton
          width="100%"
          height={48}
          borderRadius="0.25rem"
        />
        <Skeleton
          width="100%"
          height={48}
          borderRadius="0.25rem"
        />
      </Flex>
    </Flex>
  );
};
